import React, { useState } from "react"
import { Link as LinkRouter } from "react-router-dom"
import {
	Grid,
	Typography,
	Avatar,
	useTheme,
	useMediaQuery,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	IconButton,
	Button,
	Tooltip,
	Divider as VerticalDivider
} from "@material-ui/core"
import {
	ExitToApp as LogoutIcon,
	AccountCircleOutlined as ProfileIcon,
	Menu as MenuIcon,
	HelpOutline as HelpIcon,
	CardGiftcard as CardGiftcardIcon,
	PeopleOutlineTwoTone as PeopleOutlineTwoToneIcon,
	Business as AdminConsoleIcon
} from "@material-ui/icons"

import { useGlobalStateStore } from "@/store/GlobalState"
import { useReferAndEarn } from "@/hooks/useReferAndEarn"
import { usePermissionStateStore } from "@/store/PermissionState"
import AuthService from "@/services/Auth"
import AnalyticsService from "@/services/Analytics"

import { Divider } from "@/components"
import useStyles from "@/components/Navbar/styles"

import InstanceSelectorDialogue from "@/components/InstanceSelectorDialogue"
import useDidMount from "@/hooks/useDidMount"
import { colors } from "@/styles/colors"
import { letalkLinks } from "@/utils/link"
import ColorUtil from "@/utils/ColorUtil"
import { adminConsoleAppUrl } from "@/config/url"
import LetalkLogo from "@/components/SVGComponents/LetalkLogo"

type NavbarProps = {
	title: string
	onOpen?: () => void
	hiddenComponents?: {
		menuUserData?: boolean
		menuLogout?: boolean
		indicationMemberButton?: boolean
		smallScreenMenu?: boolean
	}
}

const Navbar: React.FC<NavbarProps> = (props) => {
	const {
		title,
		onOpen,
		hiddenComponents
	} = props

	const globalStateStore = useGlobalStateStore()
	const MuiTheme = useTheme()
	const classes = useStyles()
	const isSmall = useMediaQuery(MuiTheme.breakpoints.down("sm"))
	const { openReferAndEarnPage } = useReferAndEarn()
	const { checkPermission } = usePermissionStateStore()

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const openMenu = Boolean(anchorEl)

	const userStore = globalStateStore.user
	const isExtraSmall = useMediaQuery(MuiTheme.breakpoints.down("xs"))

	// const subscriptionData = globalStateStore.instance.subscriptionData
	const hasMultipleInstances = globalStateStore.user.user_instances.length > 1
	const areAllComponentsHidden =
		Object.values(hiddenComponents ?? {}).every(hiddenComponent => hiddenComponent === false)

	const canAccessAdminConsole = checkPermission("admin-console", "access")
	const isAdminConsoleDisabled = !canAccessAdminConsole

	const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget)
	}

	const handleCloseMenu = () => {
		setAnchorEl(null)
	}

	const handleLogout = async (event: React.SyntheticEvent) => {
		event.preventDefault()

		await AuthService.logout("/signin")
	}

	const handleDrawerOpen = () => {
		onOpen?.()
	}

	const handleOpenAdminConsole = () => {
		handleCloseMenu()
		AnalyticsService.capture("admin_console_opened")
		window.open(adminConsoleAppUrl.cpURL)
	}

	const handleOpenMultipleInstancesDialogue = () => {
		InstanceSelectorDialogue.open({
			actualUserInstanceId: globalStateStore.instance.instance_id,
			allUserInstances: globalStateStore.user.user_instances,
			isAdminView: globalStateStore?.user?.extra_data?.is_admin_mode
		})
	}

	const MobileScreenContent = () => {
		return (
			<>
				<IconButton color="inherit"
					onClick={handleDrawerOpen}>
					<MenuIcon />
				</IconButton>

				<Divider orientation="vertical" size={2} />
			</>
		)
	}

	const DesktopScreenContent = () => {
		return (
			<>
				<LetalkLogo
					className={classes.logoImg}
				/>

				<Divider orientation="vertical" size={2} />

				{!isSmall && (
					<Typography
						variant="button"
						color="inherit"
						className={classes.titleText}
					>
						{title}
					</Typography>
				)}
			</>
		)
	}

	useDidMount(() => {
		window.Canny("initChangelog", {
			appID: "6686b336be9f9cb0165d0796",
			position: "bottom",
			align: "left",
			theme: "light"
		})
	})

	return (
		<>
			{/* {
				subscriptionData?.isTrial && <Grid item>
					<Countdown
						expirationDate={new Date(subscriptionData.expiration_date)}
						alertText="O SEU PERÍODO DE TESTE TERMINA EM:"
						actionText="VER PLANOS"
						onAction={() => {
							window.open(letalkLinks.letalkPlans, "_blank")
						}}
					/>
				</Grid>
			} */}

			<Grid container direction="row" className={classes.navbarContainer} >
				<Grid
					container
					justifyContent="space-between"
					alignContent="center"
					alignItems="center"
				>
					<Grid item>
						<Grid
							container
							alignItems="center"
							wrap="nowrap"
						>
							{
								isExtraSmall
									? <MobileScreenContent />
									: <DesktopScreenContent />
							}
						</Grid>
					</Grid>

					<Grid item>
						<Grid
							container
							wrap="nowrap"
							alignItems="center"
							justifyContent="space-between"
						>
							<Grid item>
								<Button
									style={{ borderColor: colors.gray[0], margin: 8 }}
									size="small"
									variant="outlined"
									onClick={openReferAndEarnPage}
									startIcon={
										<CardGiftcardIcon className={classes.shakeIcon} style={{ color: colors.gray[0] }} />
									}
								>
									<Typography noWrap style={{ color: colors.gray[0], fontWeight: "bold" }}>
										Indique e Ganhe
									</Typography>
								</Button>
							</Grid>

							<Grid item>
								<Tooltip
									title={hasMultipleInstances ? "Clique para alterar a instância" : "Clique para visualizar a instância"}
								>
									<span>
										<Button
											style={{
												...ColorUtil.getBackgroundAndContrastColor("white"),
												marginLeft: "12px",
												marginRight: "12px"
											}}
											size="small"
											variant="contained"
											onClick={handleOpenMultipleInstancesDialogue}
										>
											<PeopleOutlineTwoToneIcon
												className={classes.changeInstanceButtonIcon}
											/>

											<Divider size={1} orientation="vertical" />
											{isExtraSmall
												? <Typography
													noWrap
													color="primary"
												>
													{globalStateStore.instance.instance_id}
												</Typography>
												: <Typography
													noWrap
													color="primary"
												>
													ID da instância: {globalStateStore.instance.instance_id}
												</Typography>
											}
										</Button>
									</span>
								</Tooltip>
							</Grid>

							<VerticalDivider
								style={{
									height: 32,
									margin: "0 8px",
									alignSelf: "center",
									borderLeft: `1px solid ${colors.gray[200]}`,
									borderBottom: "none",
									marginLeft: "16px"

								}}
							/>

							<Grid item>
								<IconButton
									onClick={() => { window.open(letalkLinks.wikiMainPage, "_blank") }}
								>
									<HelpIcon
										style={{
											fontSize: "24px",
											color: "white"
										}}
										fontSize="large"
									/>
								</IconButton>
							</Grid>

							<Grid item>
								<Grid container alignItems="center" justifyContent="space-between">
									{
										!isSmall &&
										<Grid item style={{
											paddingRight: 6,
											marginLeft: 8
										}}>
											<Grid
												container
												direction="column"
												alignItems="flex-end"
											>
												<Typography
													variant="h2"
													color="inherit"
													className={classes.nameText}
												>
													{userStore?.name}
												</Typography>

												<Typography
													color="inherit"
													variant="h2"
													className={classes.secondaryText}
												>
													{userStore?.email}
												</Typography>
											</Grid>
										</Grid>
									}
									<Grid item>
										<Avatar
											aria-label="more"
											aria-controls="header-menu"
											aria-haspopup="true"
											onClick={handleClickMenu}
											className={classes.profile}
											style={{
												cursor: areAllComponentsHidden ? "pointer" : "default"
											}}
										>
											{
												userStore
													?.name
													.charAt(0)
													.toUpperCase()
											}
										</Avatar>
									</Grid>

									<Grid item>
										{
											areAllComponentsHidden &&
											<Menu
												id="header-menu"
												anchorEl={anchorEl}
												keepMounted
												open={openMenu}
												onClose={handleCloseMenu}
												autoFocus={false}
											>
												{
													!hiddenComponents?.menuUserData && (
														<MenuItem
															component={LinkRouter}
															to={"/admin/profile"}
															onClick={handleCloseMenu}
														>
															<ListItemIcon>
																<ProfileIcon fontSize="small" />
															</ListItemIcon>
															<ListItemText primary="Meus dados" />
														</MenuItem>
													)
												}

												{
													!hiddenComponents?.menuUserData && (
														<Tooltip title={isAdminConsoleDisabled && "Este recurso não está disponível para o seu cargo atual."}>
															<span>
																<MenuItem
																	onClick={handleOpenAdminConsole}
																	disabled={isAdminConsoleDisabled}
																>
																	<ListItemIcon>
																		<AdminConsoleIcon fontSize="small" />
																	</ListItemIcon>
																	<ListItemText primary="Admin Console" />
																</MenuItem>
															</span>
														</Tooltip>
													)
												}

												{
													!hiddenComponents?.menuLogout && (
														<MenuItem
															component="a"
															href="#logout"
															onClick={handleLogout}
														>
															<ListItemIcon>
																<LogoutIcon fontSize="small" />
															</ListItemIcon>
															<ListItemText primary="Sair" />
														</MenuItem>
													)
												}
											</Menu>
										}
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</>
	)
}

export default Navbar
